import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AuthView } from '../../lib/auth';

interface AuthPageWrapperProps {
    pathname: 'sign-in' | 'sign-up' | 'forgot-password' | 'reset-password';
    defaultRedirect?: string;
}

/**
 * Wrapper for Neon Auth views that handles post-authentication redirect.
 *
 * After successful login, redirects to:
 * 1. The original page the user tried to access (from location.state.from)
 * 2. Or the defaultRedirect path (defaults to /default/dashboard)
 */
export default function AuthPageWrapper({ pathname, defaultRedirect = '/onboarding' }: AuthPageWrapperProps) {
    const { user, isLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Get the redirect path from location state (set by RequireAuth) or use default
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || defaultRedirect;

    useEffect(() => {
        // If user is authenticated and not loading, redirect to the intended destination
        if (user && !isLoading) {
            navigate(from, { replace: true });
        }
    }, [user, isLoading, navigate, from]);

    // Show loading while checking auth state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-700">Loading...</p>
                </div>
            </div>
        );
    }

    // If already authenticated, don't show the auth view (useEffect will redirect)
    if (user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-700">Redirecting...</p>
                </div>
            </div>
        );
    }

    // Show the Neon Auth view
    return <AuthView pathname={pathname} />;
}
